import './CrfLibrary.css';

import { useEffect, useState } from "react";
import { getInput } from "../../api/inputApi";
import BodyHeader from "../Layouts/BodyHeader/BodyHeader";
import ViewHeader from '../Layouts/ViewHeader/ViewHeader';
import { getSearchResult } from "../../api/searchApi";
import InputSelect from '../Layouts/InputField/InputSelect/inputSelect';
import InputText from '../Layouts/InputField/InputText/inputText';
import Spinner from "../Layouts/Spinner/spinner";
import ResultsTable from "../Layouts/ResultsTable/resultsTable";
import FormBuilderCart from '../FormBuilder/cart/formBuilderCart';
import NotificationBar from '../Layouts/Notification/notificationBar';
import useNotifications from '../../hooks/useNotifications';

const defaultFormData = {
  disease: "",
  subDisease: "",
  domain: "",
  subDomain: "",
  copyright: "",
  keyword: "",
};

// Fetch input data once and store it in state
const fetchData = async () => {
  const diseaseInput = await getInput("disease");
  const subDiseaseInput = await getInput("subDisease");
  const domainInput = await getInput("domain");
  const subDomainInput = await getInput("subDomain");
  return { diseaseInput, subDiseaseInput, domainInput, subDomainInput };
};

export default function CrfLibrary() {
  const [formData, setFormData] = useState({ ...defaultFormData });
  const [inputs, setInputs] = useState({
    diseaseInput: [],
    subDiseaseInput: [],
    domainInput: [],
    subDomainInput: [],
  });
  const [dropdowns, setDropdowns] = useState({
    diseasesDrpDwn: [],
    subDiseasesDrpDwn: [],
    domainsDrpDwn: [],
    subDomainsDrpDwn: [],
  });

  const [disableSubDisease, setDisableSubDisease] = useState(true);
  const [disableSubDomain, setDisableSubDomain] = useState(true);

  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);

  const { notifications, removeNotification, ...restNotifications } = useNotifications();

  useEffect(() => {
    fetchData().then((data) => {
      setInputs(data);
      setDropdowns({
        diseasesDrpDwn: data.diseaseInput,
        subDiseasesDrpDwn: data.subDiseaseInput,
        domainsDrpDwn: data.domainInput,
        subDomainsDrpDwn: data.subDomainInput,
      });
    });
  }, []);

  let filteredSubDiseases = [];
  let filteredSubDomains = [];

  const handleChange = (e, type) => {
    const value = e.target.value;
    setFormData({ ...formData, [type]: value });

    switch (type) {
      case "disease":
        if (value === "") {
          setDisableSubDisease(true);
          filteredSubDiseases = inputs.subDiseaseInput;
          setFormData({
            ...formData,
            disease: defaultFormData.disease,
            subDisease: defaultFormData.subDisease,
          });
        } else {
          filteredSubDiseases = inputs.subDiseaseInput.filter(
            (subDisease) => subDisease.diseaseId === value
          );

          setDisableSubDisease(false);
        }
        setDropdowns({
          ...dropdowns,
          subDiseasesDrpDwn: filteredSubDiseases,
        });
        break;
      case "domain":
        if (value === "") {
          setDisableSubDomain(true);
          filteredSubDomains = inputs.subDomainInput;
          setFormData({
            ...formData,
            domain: defaultFormData.domain,
            subDomain: defaultFormData.subDomain,
          });
        } else {
          filteredSubDomains = inputs.subDomainInput.filter(
            (subDomain) => subDomain.domainId === value
          );
          setDisableSubDomain(false);
        }

        setDropdowns({
          ...dropdowns,
          subDomainsDrpDwn: filteredSubDomains,
        });
        break;
      default:
        break;
    }
  };

  // console.log(formData);

  const buttonSearchHandler = async (e) => {
    e.preventDefault();

    setIsSearching(true);
    setSearchResults(null);

    const searchResult = await getSearchResult("CrfCatalog", formData);
    setSearchResults(searchResult);
    setIsSearching(false);
  };

  const buttonClearHandler = (e) => {
    setFormData({ ...defaultFormData });
    setDropdowns({
      ...dropdowns,
      //subDiseasesDrpDwn: [],
      subDomainsDrpDwn: [],
    });
    //setDisableSubDisease(true);
    setDisableSubDomain(true);
    setSearchResults(null);
    setIsSearching(false);
  };

  return (
    <>
      <BodyHeader pageHeader="CRF Library" />
      <NotificationBar notifications={notifications} closeHandler={removeNotification} />
      <FormBuilderCart />
      <ViewHeader>
        <p><strong>***Please contact the NINDS CDE Team (<a href="mailto:NINDSCDE@emmes.com">NINDSCDE@emmes.com</a>) if you encounter any search difficulties.***</strong></p>
        <p>The CRF Library contains the Case Report Form (CRF) Modules, Guidance Documents and Instrument Notice of Copyright (NOC) Documents that have been created through the NINDS CDE Project.</p>
        <p>Select any filter below to search the CRF Library.</p>
        <p>For best results, clear form between searches. In addition, when specifying NeuroRehab or Sport-Related Concussion (SRC) as the Disease, please select a Subdisease as well.</p>
        <p>NeuroRehab Comprehensive includes all NeuroRehab CDE recommendations. NeuroRehab General includes all NeuroRehab CDE recommendations that are not disease specific. All other NeuroRehab Subdiseases include recommendations specific to existing NINDS CDE project disorders.</p>
        <p>Choose your Time Frame for your SRC study (Acute (time of injury until 72 hours), Subacute (after 72 hours to 3 months), and Persistent/Chronic (3 months and greater post-concussion) or Comprehensive if your study falls across the study time frames.</p>
      </ViewHeader>
      <h4>Search Form</h4>
      <div className="crf-view-filters">
        <form id="views-exposed-form-crf-library-page">
          <InputSelect
            id="disease"
            labelText="Disease Name"
            changeHandler={(e) => handleChange(e, "disease")}
            inputArr={dropdowns.diseasesDrpDwn}
            selectedValue={formData.disease}
          />
          <InputSelect
            id="subDisease"
            labelText="Subdisease Name"
            changeHandler={(e) =>
              setFormData({ ...formData, subDisease: e.target.value })
            }
            inputArr={dropdowns.subDiseasesDrpDwn}
            //disableVal={disableSubDisease}
            selectedValue={formData.subDisease}
          />
          <InputSelect
            id="domain"
            labelText="Domain Name"
            changeHandler={(e) => handleChange(e, "domain")}
            inputArr={dropdowns.domainsDrpDwn}
            selectedValue={formData.domain}
          />
          <InputSelect
            id="subDomain"
            labelText="Subdomain Name"
            changeHandler={(e) =>
              setFormData({ ...formData, subDomain: e.target.value })
            }
            inputArr={dropdowns.subDomainsDrpDwn}
            disableVal={disableSubDomain}
            selectedValue={formData.subDomain}
          />
          <InputSelect
            id="copyright"
            labelText="Copyright"
            changeHandler={(e) =>
              setFormData({ ...formData, copyright: e.target.value })
            }
            inputArr={["True", "False"]}
          />
          <InputText
            id="keyword"
            labelText="Keyword"
            inputValue={formData.keyword}
            changeHandler={(e) =>
              setFormData({ ...formData, keyword: e.target.value })
            }
          />

          <div className="button-section">
            <button
              type="submit"
              id="submit"
              value="Search"
              onClick={(e) => buttonSearchHandler(e)}
            >
              Search
            </button>

            <button
              type="reset"
              id="clear"
              value="clear"
              onClick={(e) => buttonClearHandler(e)}
            >
              Clear
            </button>
          </div>
        </form>
        <hr />
      </div>
      <div className="crf-view-content">
        {isSearching && <Spinner />}
        {searchResults && (
          <ResultsTable data={searchResults} reportType="CRF" notificationsContext={restNotifications} />
        )}
      </div>
    </>
  );
}
